import isEmpty from "lodash.isempty";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import { ExportOptionsModal } from "src/features/observationsAdmin/modals";
import GradingFilter from "src/features/reporting/components/GradingFilter/GradingFilter";
import ExportDropdown from "src/features/reporting/components/ExportDropdown/ExportDropdown";
import { DEFAULT_REPORT_TABLE_FILTERS } from "src/features/reporting/constants";
import {
	MonthlyRegionReport,
	MonthlySummary
} from "src/features/reporting/containers";
import MonthlyAreaReport from "src/features/reporting/containers/MonthlyReport/MonthlyAreaReport/MonthlyAreaReport";
import MonthlyStoreReport from "src/features/reporting/containers/MonthlyReport/MonthlyStoreReport/MonthlyStoreReport";
import MonthlyUserReport from "src/features/reporting/containers/MonthlyReport/MonthlyUserReport/MonthlyUserReport";
import ReportTableFilters from "src/features/reporting/containers/ReportTableFilters/ReportTableFilters";
import { useMonthSummary } from "src/features/reporting/hooks";
import { useObservationFormYears } from "src/features/reporting/hooks";
import useAllAreasObservationsReport from "src/features/reporting/hooks/useAllAreasObservationsReport/useAllAreasObservationsReport";
import useAllStoresObservationsReport from "src/features/reporting/hooks/useAllStoresObservationsReport/useAllStoresObservationsReport";
import usePreviousObservationFormQuery from "src/hooks/useObservationForm/useFilterFormInstance/useFilterFormInstance";
import usePreviousCompletedObservations from "src/hooks/usePreviousCompletedObservations/usePreviousCompletedObservations";
import usePublishedForm from "src/hooks/usePublishedForm/usePublishedForm";
import { adaptFormType } from "src/features/observationsAdmin/utils";
import http from "src/libs/http";
import { ReportAnswerModal } from "src/modals";
import Error404 from "src/pages/Error404/Error404";
import JsonDebug from "src/utils/PreetyJSON";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import shouldOpenObservationModal from "src/features/reporting/utils/shouldOpenObservationModal/shouldOpenObservationModal";

export type SelectedExport =
	| "ALL_USERS"
	| "ALL_STORES"
	| "ALL_OBSERVATIONS"
	| "BY_USER"
	| undefined
	| null;

const ReportDetail = () => {
	const dashboardDownloadRef = useRef<(() => void) | null>(null);

	const handleDownloadReady = useCallback((fn: () => void) => {
		dashboardDownloadRef.current = fn;
	}, []);

	const [selectedExport, setSelectedExport] =
		useState<SelectedExport>(undefined);

	const [isReportAnswerModalOpen, setIsReportAnswerModalOpen] =
		useState<boolean>(false);
	const [isExportConfirmationModalOpen, setIsExportConfirmationModalOpen] =
		useState<boolean>(false);

	const params = useParams<{
		formId: string;
	}>();

	const formId = parseInt(params.formId);

	const { data: form, error: formError } = usePublishedForm(formId);
	const { data: monthlySummary } = useMonthSummary(formId);
	const { data: years } = useObservationFormYears(formId);

	const methods = useForm<ReportDetailForm>({
		defaultValues: {
			formId,
			filters: DEFAULT_REPORT_TABLE_FILTERS
		}
	});

	const { control, setValue } = methods;

	const filters = useWatch({ control, name: "filters" });

	const {
		areaId,
		instanceId,
		userId,
		regionId,
		storeId,
		month,
		year,
		showCompliantReport,
		showObservationsPassedThresholdReport,
		showGradingReport,
		calendarMonth
	} = useWatch({
		control,
		name: "filters"
	});

	const { download: downloadAllAreaObservations } =
		useAllAreasObservationsReport({ formId, areaId });

	const { download: downloadAllStoreObservations } =
		useAllStoresObservationsReport({ formId, storeId });

	const allFields = useWatch({ control });

	const { refetch: fetchFormInstance } = usePreviousObservationFormQuery({
		formId,
		storeId,
		userId,
		month,
		year,
		complete: true,
		compliant: form?.observation_type === "INDIVIDUAL" ? true : false,
		enabled: Boolean(userId),
		onSuccess: (instances) => {
			if (isEmpty(instances)) return;

			// `all-versions` returns newest-first, so `instances[0]` is the
			// most recent submission for the clicked cell. The modal must open
			// on that one ("the most recent observation for the date selected")
			// and `/previous/` then yields every older instance for the same
			// subject to page back through. Picking the last element here would
			// land on the OLDEST version and hide any newer ones.
			setValue("filters.instanceId", instances[0].id);
		}
	});

	const { data: previousObservations } = usePreviousCompletedObservations(
		{ observationInstanceId: instanceId, year },
		{
			enabled: Boolean(instanceId)
		}
	);

	// `/instances/{id}/previous/` returns instances strictly older than
	// the picked one, so on its own the modal would render history but
	// hide the cell's own instance — including the just-clicked
	// submission. Append it explicitly so the carousel lands on the
	// current instance by default (its `activeIndex` is `length - 1`).
	//
	// We filter `instanceId` out before appending so it always lands at
	// the END of the list, even on the defensive case where the backend
	// changes its semantics and `/previous/` starts including the
	// reference. `instanceId != null` rather than truthy so a legit
	// id of `0` doesn't silently skip the append.
	const observations =
		instanceId !== null && instanceId !== undefined && previousObservations
			? [...previousObservations.filter((id) => id !== instanceId), instanceId]
			: previousObservations;

	useEffect(() => {
		if (!selectedExport) return;

		setIsExportConfirmationModalOpen(true);
	}, [selectedExport]);

	useUpdateEffect(() => {
		if (!form) return;

		const { observation_type } = form;

		if (observation_type === "INDIVIDUAL") {
			setValue("filters.showCompliantReport", true);
		} else {
			setValue("filters.showObservationsPassedThresholdReport", false);
		}
	}, [form]);

	useUpdateEffect(() => {
		if (!form) return;

		// Only open the observation modal when a specific observation is
		// drilled into (a month cell click). Selecting a store/employee from
		// the search box sets storeId without a month — that must filter the
		// table, not open the last completed observation.
		if (
			shouldOpenObservationModal(form.observation_type, {
				storeId,
				userId,
				month
			})
		) {
			fetchFormInstance().then(() => setIsReportAnswerModalOpen(true));
		}
	}, [storeId, userId, month, form]);

	const handlecloseReportAnswerModalClick = () => {
		setValue("filters.month", undefined);

		if (!form) return;

		if (checkObservationFormType(form.observation_type) === "user") {
			setValue("filters.userId", undefined);
		}

		if (checkObservationFormType(form.observation_type) === "store") {
			setValue("filters.storeId", undefined);
		}

		setIsReportAnswerModalOpen(false);
	};

	const closeExportModal = () => {
		setIsExportConfirmationModalOpen(false);
		setSelectedExport(null);
	};

	const downloadObservationsReport = () => {
		if (areaId && storeId && userId) {
			setSelectedExport("BY_USER");
			return;
		}

		if (areaId && storeId) {
			return downloadAllStoreObservations();
		}

		if (areaId) {
			return downloadAllAreaObservations();
		}
	};

	const showMonthlyUserReport =
		regionId &&
		areaId &&
		storeId &&
		checkObservationFormType(form?.observation_type) === "user";

	// Store-based forms have no deeper table than the store-by-month grid, so
	// when a store is selected (e.g. from the search) we keep this table
	// rendered and scope it to that store — mirroring how the employee search
	// drills user-based forms into MonthlyUserReport. User-based forms still
	// hand off to MonthlyUserReport once a store is picked, so they keep the
	// original `!storeId` guard.
	const isStoreBasedForm =
		!!form && checkObservationFormType(form.observation_type) === "store";

	const showMonthlyStoreReport =
		form && regionId && areaId && (!storeId || isStoreBasedForm);

	if (http.isAxiosError(formError) && formError.response?.status === 404) {
		return <Error404 />;
	}

	return (
		<div>
			<FormProvider {...methods}>
				{form && (
					<div className="mb-6">
						<h2 className="font-heading text-xl font-semibold text-foreground">
							{form.name}
						</h2>
						<p className="text-sm text-muted-foreground">
							{adaptFormType(form.observation_type)}
						</p>
					</div>
				)}

				{isExportConfirmationModalOpen && (
					<ExportOptionsModal
						formId={formId}
						selectedExport={selectedExport}
						isOpen={isExportConfirmationModalOpen}
						onCancel={closeExportModal}
						showDateFilters={!userId}
						userId={userId}
					/>
				)}

				{form && observations && isReportAnswerModalOpen && (
					<ReportAnswerModal
						isOpen={isReportAnswerModalOpen}
						onClose={handlecloseReportAnswerModalClick}
						observations={observations}
						form={form}
						onExportAllObservationsClick={downloadObservationsReport}
						showGradingReportState={showGradingReport}
					/>
				)}

				{form?.observation_type === "GREAT_CONVERSATION" && <GradingFilter />}

				{form && monthlySummary && (
					<MonthlySummary
						observationType={form.observation_type}
						numStores={monthlySummary.stores}
						numEmployees={monthlySummary.employees}
						numObservationsCompleted={monthlySummary.completed}
						numObservationsCompliant={monthlySummary.compliant}
						numStoresPassedThreshold={monthlySummary.stores_passed_threshold}
						numEmployeesGraded={monthlySummary.graded}
					/>
				)}

				<hr className="border-border my-8" />

				{form?.observation_type === "GREAT_CONVERSATION" && (
					<>
						{showGradingReport ? (
							<h2 className="font-heading text-lg font-medium text-foreground mb-8">
								Grading table
							</h2>
						) : (
							<h2 className="font-heading text-lg font-medium text-foreground mb-8">
								Full Report
							</h2>
						)}
					</>
				)}

				<JsonDebug data={allFields} />

				{form && years && (
					<ReportTableFilters
						observationType={form.observation_type}
						years={years.results.map(({ year }) => year)}
					/>
				)}

				{form && !regionId && (
					<MonthlyRegionReport
						formId={formId}
						observationType={form?.observation_type}
						year={year}
						showCompliantReport={showCompliantReport}
						showObservationsPassedThresholdReport={
							showObservationsPassedThresholdReport
						}
						calendarMonth={calendarMonth}
						calendarYear={year}
						onDownloadReady={handleDownloadReady}
					/>
				)}

				{form && regionId && !areaId && !storeId && (
					<MonthlyAreaReport
						formId={formId}
						year={year}
						regionId={regionId}
						showCompliantReport={showCompliantReport}
						showObservationsPassedThresholdReport={
							showObservationsPassedThresholdReport
						}
						observationType={form.observation_type}
						calendarMonth={calendarMonth}
						calendarYear={year}
						onDownloadReady={handleDownloadReady}
					/>
				)}

				{showMonthlyStoreReport && (
					<MonthlyStoreReport
						formId={formId}
						year={year}
						areaId={areaId}
						storeId={storeId}
						showCompliantReport={showCompliantReport}
						showObservationsPassedThresholdReport={
							showObservationsPassedThresholdReport
						}
						observationType={form.observation_type}
						calendarMonth={calendarMonth}
						calendarYear={year}
						onDownloadReady={handleDownloadReady}
					/>
				)}

				{showMonthlyUserReport && (
					<MonthlyUserReport
						formId={formId}
						year={year}
						storeId={storeId}
						areaId={areaId}
						compliancy={showCompliantReport}
						observationType={form?.observation_type}
						calendarMonth={calendarMonth}
						calendaryYear={year}
						onDownloadReady={handleDownloadReady}
					/>
				)}

				{form && (
					<ExportDropdown
						showDashboard={form.observation_type !== "GREAT_CONVERSATION"}
						onExportDashboard={() => dashboardDownloadRef.current?.()}
						showUsers={form.observation_type === "INDIVIDUAL"}
						onExportUsers={() => setSelectedExport("ALL_USERS")}
						showStores={form.observation_type === "INDIVIDUAL"}
						onExportStores={() => setSelectedExport("ALL_STORES")}
						showObservations={
							(!filters.userId && !filters.storeId) ||
							form.observation_type === "GREAT_CONVERSATION"
						}
						onExportObservations={() => setSelectedExport("ALL_OBSERVATIONS")}
					/>
				)}
			</FormProvider>
		</div>
	);
};

export default ReportDetail;
